import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import Fade from 'react-bootstrap/Fade';
import { throttle } from 'lodash';
import API from '../../../utils/API';
import styles from './Contacts.module.css';

const handleContactCircles = (contacts) => {
    if (!contacts) return null;
    const visible = contacts.length > 2 ? contacts.slice(-2) : contacts;
    return visible.map((contact) => (
        <div key={contact.uid} className={styles.circle}>
            <img src={contact.profile_pic || '/images/BitBlue-Person-Default-Profile-Picture.png'} alt="contact" />
        </div>
    ));
};

const handleContactName = (contact) => {
    const { name_first: first, name_last: last } = contact;
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    if (last) return last;
    return 'User';
};

const handleDownloadClick = async (contact) => {
    const date = Date.now();
    const enterprise = contact?.route?.includes('/') && contact?.route?.substring(0, contact?.route?.indexOf('/'));

    const userAnalytics = {
        date: date,
        device: window.navigator.userAgent
    };
    
    const entAnalytics = {
        date: date,
        data: "Contact Download",
        device: window.navigator.userAgent,
        username: contact?.vcard?.username,
        type: "button_click"
    };

    await API.updateDownloadCount({ uid: contact.uid, enterprise: enterprise, userAnalytics: userAnalytics, entAnalytics: entAnalytics });
};

// Main Contacts Settings Component
export default function Contacts() {

    const [ contactsObj, setContactsObj ] = useState({contacts: null, last: ''});
    const [ open, setOpen ] = useState('');
    const menuRef = useRef();

    useEffect(() => {
        const handleGetContacts = async () => {
            try {
                const res = await API.getContacts("");
                if (res && res?.length > 1) {
                    setContactsObj(prev => ({...prev, contacts: res, last: res[res.length - 1].vcard.name_last}));
                } else {
                    setContactsObj({contacts: [], last: ''})
                };
            } catch (err) {
                console.error(err);
            };
        };
    
        if (!contactsObj.contacts) {
            handleGetContacts();
        };
    
        return () => {
            contactsObj.contacts?.forEach(contact => {
                if (contact.vcf) {
                    URL.revokeObjectURL(contact.vcf);
                }
            });
        };
    }, [contactsObj.contacts]);
            
    const handleScroll = async (e) => {
        e.preventDefault();
        if (e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight && contactsObj.last !== '') {
            try {
                const res = await API.getContacts(contactsObj.contacts[contactsObj.contacts.length - 1].name_last);
                if (res) {
                    setContactsObj(prev => ({...prev, contacts: prev.contacts.concat(res), last: res.length < 10 ? '' : res[res.length - 1].vcard.name_last}));
                } else {
                    setContactsObj(prev => ({...prev, last: ''}))
                };
            } catch (err) {
                console.log(err);
            };
        };
    };

    const handleContactMenu = (uid) => {
        setOpen((prev) => (prev === uid ? '' : uid));
    };

    useEffect(() => {
        const menu = menuRef.current;
        const handleClickOutside = e => {
            if (menu && !menu.contains(e.target)) {
                setOpen('');
            };
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    },[]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerInner}>
                    <div className={styles.title}>Contacts</div>
                    <div className={styles.subtitle}>Grow your circle!</div>
                </div>
                <div className={styles.circles}>
                    {contactsObj?.contacts?.length > 2 ? <div className={styles.circleDark}><i className='far fa-plus' />{contactsObj.contacts.length - 2}</div> : <></> }
                    {handleContactCircles(contactsObj.contacts)}
                </div>
            </div>
            <div className={styles.list} onScroll={throttle(handleScroll, 100, {leading: true})}>
                {contactsObj.contacts?.length > 0 ? contactsObj.contacts.map((contact) => (
                    <div className={styles.contact} key={contact.uid}>
                        <div className={styles.contactImage}>
                            <img src={contact.profile_pic ? contact.profile_pic : '/images/BitBlue-Person-Default-Profile-Picture.png'} alt='contact pic' />
                        </div>
                        <div className={styles.contactName}>
                            {handleContactName(contact)}
                        </div>
                        <div className={styles.contactButtons}>
                            <a className={styles.contactBtn} href={contact.vcf} download={`${contact.vcard.username}.vcf`} title='Download Contact' onClick={() => handleDownloadClick(contact)}><i className='far fa-download' /></a>
                            <Link className={styles.contactBtn} to={{pathname: '/inbox', state: { userToMessage: contact.uid, thread: '', type: 'new' }}} title="Send Message"><i className='far fa-message' /></Link>
                            <Link className={styles.contactBtn} to={`/${contact.route}`} title="View Digital Card"><i className='far fa-address-card' /></Link>
                        </div> 
                        <div className={styles.contactButtons992}>
                            <button className={styles.contactMenuBtn} onClick={() => handleContactMenu(contact.uid)}><i className='fas fa-ellipsis-vertical' /></button>
                            <Fade in={open === contact.uid} style={open === contact.uid ? {display: 'flex'} : {display: 'none'}}>
                                <div className={styles.contactMenuPosition} ref={menuRef}>
                                    <div className={styles.contactMenu}>
                                        <a className={styles.contactMenuOption}  title='Download Contact'><i className='far fa-download' /> Download</a>
                                        <Link className={styles.contactMenuOption} to='/inbox' state={{ userToMessage: contact.uid, thread: '', type: 'new' }} title="Send Message"><i className='far fa-message' /> Message</Link>
                                        <Link className={styles.contactMenuOption} to={`/${contact.route}`} title="View Digital Card"><i className='far fa-address-card' />View Card</Link>
                                    </div>
                                </div>
                            </Fade>
                        </div>
                    </div>
                ))
                :
                <>
                <div className={styles.contactFake}>
                    <div className={styles.contactImage}>
                        <img src='/images/BitBlue-Person-Default-Profile-Picture.png' alt='contact pic' />
                    </div>
                    <div className={styles.contactName}>
                        John Doe
                    </div>
                    <div className={styles.contactButtons}>
                        <div className={styles.contactBtn} title='Download Contact'><i className='far fa-download' /></div>
                        <div className={styles.contactBtn} title="Send Message"><i className='far fa-message' /></div>
                        <div className={styles.contactBtn} title="View Digital Card"><i className='far fa-address-card' /></div>
                    </div> 
                    <div className={styles.contactButtons992}>
                        <button className={styles.contactMenuBtn}><i className='fas fa-ellipsis-vertical' /></button>
                    </div>
                </div>
                <div className={styles.noContacts}>You have no contacts.</div>
                </>
                }
            </div>
        </div>
    );
};